import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import { Typography, Tag, Spin, Space, Breadcrumb } from "antd";
import {
  ClockCircleOutlined,
  UserOutlined,
  EyeOutlined,
  HomeOutlined,
  ReadOutlined,
} from "@ant-design/icons";
import { api, endpoints } from "../../utils/axiosInstance";
import { Row, Col } from "antd";

const { Title, Text, Paragraph } = Typography;

const BreadcrumbContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  display: flex;
  justify-content: center;

  .ant-breadcrumb {
    font-size: 16px;

    a {
      color: rgba(255, 255, 255, 0.85);

      &:hover {
        color: white;
      }
    }

    .ant-breadcrumb-link {
      color: rgba(255, 255, 255, 0.85);
    }

    .ant-breadcrumb-separator {
      margin: 0 12px;
      color: rgba(255, 255, 255, 0.85);
    }

    span:last-child {
      color: rgba(255, 255, 255, 0.65);
    }
  }
`;

const HeroSection = styled.div`
  position: relative;
  padding: 80px 20px 60px;
  text-align: center;
  color: white;
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  min-height: 400px;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: ${(props) =>
      props.backgroundImage
        ? `url(${props.backgroundImage})`
        : "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)"};
    background-size: cover;
    background-position: center;
    filter: brightness(0.6);
    z-index: 1;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.7) 0%,
      rgba(0, 0, 0, 0.4) 100%
    );
    z-index: 2;
  }

  h1,
  div {
    position: relative;
    z-index: 3;
  }

  h1 {
    color: white;
    margin-bottom: 0;
    font-size: 48px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  }
`;

const StyledDescription = styled.div`
  font-size: 20px;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.4);
  background: rgba(0, 0, 0, 0.2);
  padding: 12px 24px;
  border-radius: 8px;
  backdrop-filter: blur(4px);
  letter-spacing: 0.5px;
  font-weight: 500;
  width: 100%;
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: #f9f5f0;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: -60px;
  position: relative;
  z-index: 10;
`;

const RecipeTag = styled.div`
  background-color: #ff4b4b;
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  display: inline-block;
  margin-bottom: 16px;
  font-weight: 500;
`;

const MetaInfo = styled.div`
  display: flex;
  gap: 24px;
  margin: 24px 0;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;

  .anticon {
    font-size: 16px;
  }
`;

const SectionImage = styled.img`
  width: 100%;
  max-height: 400px;
  object-fit: cover;
  border-radius: 12px;
  margin: 24px 0;
`;

const TagsContainer = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #eee;
`;

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedBlogs, setRelatedBlogs] = useState([]);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        const response = await api.get(endpoints.blogs.bySlug(slug));
        setBlog(response);
      } catch (error) {
        console.error("Error fetching blog:", error);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlog();
    }
  }, [slug]);

  useEffect(() => {
    if (blog?._id) {
      const fetchRelatedBlogs = async () => {
        try {
          const response = await api.get(`/blogs/related-by-tags/${blog._id}`);
          setRelatedBlogs(response);
        } catch (error) {
          console.error("Lỗi khi lấy blog liên quan theo tag:", error);
        }
      };

      fetchRelatedBlogs();
    }
  }, [blog]);

  const getBreadcrumbItems = () => {
    return [
      {
        title: (
          <Link to="/blogs">
            <HomeOutlined /> Blog
          </Link>
        ),
      },
      {
        title: (
          <Link to="/blogs/all-blogs">
            <ReadOutlined /> All Stories
          </Link>
        ),
      },
      {
        title: blog?.title || "Blog Detail",
      },
    ];
  };

  if (loading) {
    return (
      <PageWrapper>
        <ContentWrapper style={{ textAlign: "center", padding: "100px 0" }}>
          <Spin size="large" />
        </ContentWrapper>
      </PageWrapper>
    );
  }

  if (!blog) {
    return (
      <PageWrapper>
        <ContentWrapper>
          <Title level={2}>Không tìm thấy bài viết</Title>
        </ContentWrapper>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <HeroSection backgroundImage={blog.coverImage}>
        <Title level={1}>{blog.title}</Title>
        <StyledDescription>{blog.summary}</StyledDescription>
        <BreadcrumbContainer>
          <Breadcrumb items={getBreadcrumbItems()} />
        </BreadcrumbContainer>
      </HeroSection>

      <ContentWrapper>
        <RecipeTag>BLOG</RecipeTag>

        <MetaInfo>
          <MetaItem>
            <ClockCircleOutlined />
            <Text>{blog.date}</Text>
          </MetaItem>
          <MetaItem>
            <UserOutlined />
            <Text>{blog.author?.full_name || "Admin"}</Text>
          </MetaItem>
          <MetaItem>
            <EyeOutlined />
            <Text>{blog.views} lượt xem</Text>
          </MetaItem>
        </MetaInfo>

        {blog.sections?.map((section, index) => (
          <div key={index}>
            {section.heading && (
              <Title level={3} style={{ marginTop: "32px" }}>
                {section.heading}
              </Title>
            )}
            <Paragraph style={{ fontSize: "16px", lineHeight: "1.8" }}>
              {section.text}
            </Paragraph>
            {section.image && (
              <SectionImage
                src={section.image}
                alt={section.heading || "Blog section"}
              />
            )}
          </div>
        ))}

        <TagsContainer>
          <Space size={[0, 8]} wrap>
            {blog.tags?.map((tag, index) => (
              <Tag key={index} color="blue">
                {tag}
              </Tag>
            ))}
          </Space>
        </TagsContainer>
      </ContentWrapper>
      {relatedBlogs.length > 0 && (
        <div style={{ marginTop: "60px", padding: "0 20px" }}>
          <Title level={3}>Bài viết liên quan</Title>
          <Row gutter={[24, 24]}>
            {relatedBlogs.map((item) => (
              <Col xs={24} sm={12} md={8} key={item._id}>
                <Link
                  to={`/blogs/detail/${item.slug}`}
                  onClick={() => window.scrollTo(0, 0)}
                  style={{
                    display: "block",
                    border: "1px solid #eee",
                    borderRadius: "12px",
                    overflow: "hidden",
                    textDecoration: "none",
                    color: "#333",
                    height: "100%",
                    backgroundColor: "white", // ✅ Thêm dòng này
                    transition: "box-shadow 0.3s, transform 0.2s",
                    ":hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {item.coverImage && (
                    <img
                      src={item.coverImage}
                      alt={item.title}
                      style={{
                        width: "100%",
                        height: "160px",
                        objectFit: "cover",
                      }}
                    />
                  )}
                  <div style={{ padding: "12px" }}>
                    <h4 style={{ marginBottom: "8px" }}>{item.title}</h4>
                    <p style={{ color: "#666", fontSize: "14px" }}>
                      {item.summary?.slice(0, 80)}...
                    </p>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      {item.date}
                    </Text>
                  </div>
                </Link>
              </Col>
            ))}
          </Row>
        </div>
      )}
    </PageWrapper>
  );
};

export default BlogDetailPage;
